import { DialogContentText, LoadingButton, useDialogState } from '@cmg/design-system';
import { FormikSelectField, FormikTextField } from '@cmg/design-system-formik';
import type { FormikHelpers } from 'formik';
import { Form, FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { Fragment } from 'react';
import * as yup from 'yup';

import {
  DEFAULT_IMPERSONATION_REDIRECT_APP,
  IMPERSONATION_REDIRECT_APP_LABELS,
  ImpersonationRedirectApp,
} from '../../../../../../common/api/systemManagementApiClient';
import { ServerErrorAlert } from '../../../../../../design-system/alert/ServerErrorAlert';
import { ConfirmationDialog } from '../../../../../../design-system/dialog/confirmation/ConfirmationDialog';
import { FormDialogContent } from '../../../../../../design-system/dialog/FormDialogContent';
import { FormDialogContentItem } from '../../../../../../design-system/dialog/FormDialogContentItem';
import { useCreateImpersonationSession } from './useCreateImpersonationSession';

export type Props = Readonly<{
  onReset: () => void;
  isLoading: Readonly<boolean>;
  targetTenantSubdomain: Readonly<string>;
  targetUserId: Readonly<string>;
}>;

const POPUP_BLOCKED_ERROR = 'Failed to open the impersonation window. Allow pop-ups and try again.';
const SUBMIT_ERROR = 'Unable to start impersonation. Please try again.';

type ImpersonateFormValues = Readonly<{
  redirectApp: ImpersonationRedirectApp;
  ticket: string;
}>;

const INITIAL_FORM_VALUES: ImpersonateFormValues = {
  redirectApp: DEFAULT_IMPERSONATION_REDIRECT_APP,
  ticket: '',
};

const ITSM_TICKET_REGEX = /^ITSM-\d+$/;

const impersonateValidationSchema = yup.object().shape({
  ticket: yup
    .string()
    .trim()
    .required('Ticket is required')
    .matches(ITSM_TICKET_REGEX, 'Ticket must reference a valid ITSM Jira ticket (e.g. ITSM-1234).'),
});

const openLaunchWindow = () => {
  const launchWindow = globalThis.open('about:blank', '_blank');
  if (launchWindow) {
    launchWindow.opener = null;
  }
  return launchWindow;
};

export const ImpersonateAction: FC<Props> = ({
  onReset,
  isLoading,
  targetTenantSubdomain,
  targetUserId,
}) => {
  const { isOpen: isDialogOpen, onClose: closeDialog, onOpen: openDialog } = useDialogState();
  const {
    createSession,
    error,
    isLoading: isCreatingImpersonationSession,
    reset: resetCreateImpersonationSession,
  } = useCreateImpersonationSession();

  const handleCloseDialog = () => {
    closeDialog();
    resetCreateImpersonationSession();
    onReset();
  };

  const submitImpersonation = async (
    values: ImpersonateFormValues,
    helpers: FormikHelpers<ImpersonateFormValues>
  ) => {
    helpers.setStatus(null);
    let launchWindow: Window | null = null;

    try {
      launchWindow = openLaunchWindow();
      if (!launchWindow) {
        helpers.setStatus(POPUP_BLOCKED_ERROR);
        return;
      }

      const sessionUrl = await createSession({
        targetTenantSubdomain,
        targetUserId,
        app: values.redirectApp,
        ticket: values.ticket,
      });

      if (!sessionUrl) {
        launchWindow?.close();
        return;
      }

      launchWindow.location.href = sessionUrl;
      handleCloseDialog();
    } catch {
      launchWindow?.close();
      helpers.setStatus(SUBMIT_ERROR);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const formik = useFormik<ImpersonateFormValues>({
    initialValues: INITIAL_FORM_VALUES,
    validationSchema: impersonateValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: submitImpersonation,
  });

  const visibleError = formik.status ?? error;
  const isActionLoading = isLoading || isCreatingImpersonationSession || formik.isSubmitting;

  const handleOpenDialog = () => {
    formik.resetForm({ values: INITIAL_FORM_VALUES, status: null });
    resetCreateImpersonationSession();
    openDialog();
  };

  return (
    <Fragment>
      <LoadingButton
        variant="outlined"
        color="primary"
        disabled={isActionLoading}
        loading={isActionLoading}
        onClick={handleOpenDialog}
      >
        Impersonate
      </LoadingButton>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        isLoading={isActionLoading}
        title="Impersonate"
        cancelButtonLabel="Cancel"
        onCancel={handleCloseDialog}
        submitButtonLabel="Impersonate"
        submitButtonColor="primary"
        onSubmit={() => formik.handleSubmit()}
        content={
          <FormikProvider value={formik}>
            <Form noValidate>
              <FormDialogContent
                error={visibleError && <ServerErrorAlert title="Error" error={visibleError} />}
                items={
                  <Fragment>
                    <FormDialogContentItem
                      value={
                        <DialogContentText>
                          You are about to impersonate this user. Review the settings and continue.
                        </DialogContentText>
                      }
                    />
                    <FormDialogContentItem
                      value={
                        <FormikSelectField
                          label="Redirect App"
                          fullWidth
                          required
                          name="redirectApp"
                          options={Object.values(ImpersonationRedirectApp).map(value => ({
                            label: IMPERSONATION_REDIRECT_APP_LABELS[value],
                            value,
                          }))}
                        />
                      }
                    />
                    <FormDialogContentItem
                      value={
                        <FormikTextField
                          label="ITSM Ticket"
                          fullWidth
                          required
                          placeholder="ITSM-1234"
                          name="ticket"
                        />
                      }
                    />
                  </Fragment>
                }
              />
            </Form>
          </FormikProvider>
        }
      />
    </Fragment>
  );
};
